import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, ArrowRight, Music } from 'lucide-react';
import { motion } from 'framer-motion';
import { BlurFade } from './ui/blur-fade';

// Elegant Text Animation Component
const AnimatedTitle = ({ text }) => {
  const [isVisible, setIsVisible] = useState(true);
  const characters = text.split('');

  useEffect(() => {
    let timeout;
    
    if (isVisible) {
      timeout = setTimeout(() => setIsVisible(false), 4000);
    } else {
      timeout = setTimeout(() => setIsVisible(true), 500);
    }

    return () => clearTimeout(timeout);
  }, [isVisible]);

  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.03,
      },
    },
    hidden: {
      transition: {
        staggerChildren: 0.02,
        staggerDirection: -1,
      },
    },
  };

  const charVariants = {
    visible: {
      opacity: 1,
      filter: 'blur(0px)',
      transition: { duration: 0.3, ease: 'easeOut' },
    },
    hidden: {
      opacity: 0,
      filter: 'blur(8px)',
      transition: { duration: 0.25, ease: 'easeIn' },
    },
  };

  return (
    <motion.h2
      className="text-3xl sm:text-4xl md:text-5xl font-light text-white tracking-tight inline-flex flex-wrap justify-center"
      variants={containerVariants}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
    >
      {characters.map((char, index) => (
        <motion.span
          key={index}
          variants={charVariants}
          className="inline-block"
          style={{ whiteSpace: char === ' ' ? 'pre' : 'normal' }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </motion.h2>
  );
};

const Footer = () => {
  const socialIcons = [
    { Icon: Instagram, href: 'https://www.instagram.com/tanertorun.limitless/', color: 'hover:text-pink-500', label: 'Instagram' },
    { Icon: Youtube, href: 'https://www.youtube.com/@zihnef%C4%B1s%C4%B1ldayanadam', color: 'hover:text-[#FF0000]', label: 'YouTube' },
    { Icon: Music, href: 'https://open.spotify.com/show/4CaRzSjI2b9ALAqA4VLjKH?si=FSbRYW5oTriuf9b0WeD7mw', color: 'hover:text-[#1DB954]', label: 'Spotify' },
    { Icon: null, href: 'https://www.tiktok.com/@zihnefisildayanadam', color: 'hover:text-white', label: 'TikTok', isSvg: true },
  ];

  return (
    <footer className="bg-black text-white">
      {/* Elegant CTA Section with BlurFade */}
      <div className="relative py-20 md:py-24 overflow-hidden">
        {/* Subtle ambient glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-900/20 rounded-full filter blur-[180px]"></div>
        
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          
          {/* Elegant small label */}
          <BlurFade delay={0} inView yOffset={10} blur="8px">
            <p className="text-purple-400/80 text-sm tracking-[0.3em] uppercase mb-6 font-light">
              Dönüşüm Başlamak Üzere
            </p>
          </BlurFade>
          
          {/* Main Title - Elegant Blur Animation (fixed height to prevent layout shift) */}
          <div className="h-[56px] md:h-[72px] lg:h-[80px] mb-4 flex items-center justify-center overflow-hidden">
            <AnimatedTitle text="İçinizdeki Sese Kulak Verin" />
          </div>
          
          {/* Dramatic Question - Subtle Italic */}
          <BlurFade delay={0.2} inView yOffset={12} blur="6px">
            <p className="text-lg md:text-xl text-gray-400 font-light italic mb-8">
              O eşiğin önünde daha ne kadar bekleyeceksiniz?
            </p>
          </BlurFade>
          
          {/* Quote Text - Refined */}
          <BlurFade delay={0.3} inView yOffset={15} blur="8px">
            <div className="max-w-xl mx-auto mb-8 space-y-3">
              <p className="text-gray-400 text-base leading-relaxed font-light">
                Tereddütleriniz bir fısıltıdan ibaret. 
                Oysa içinizdeki potansiyelin sesi{' '}
                <span className="text-gray-200">bir okyanus gibi gürlüyor.</span>
              </p>
            </div>
          </BlurFade>
          
          {/* Elegant Button */}
          <BlurFade delay={0.4} inView yOffset={15}>
            <Link to="/egitimler">
              <button className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/[0.03] border border-white/10 hover:border-white/25 hover:bg-white/[0.06] transition-all duration-500">
                <span className="text-white text-base font-light tracking-wide">
                  Hemen Eğitimlere Katılın
                </span>
                <ArrowRight className="w-4 h-4 text-purple-300 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </Link>
          </BlurFade>
        </div>
      </div>

      {/* Main Footer Content with Mountain Background */}
      <div className="relative">
        {/* Mountain Background Image - positioned to show peak */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url('/assets/images/tanertorun-footer.png')`,
            backgroundPosition: 'center 40%',
          }}
        >
          {/* Top gradient - for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 via-40% to-transparent"></div>
          
          {/* Bottom gradient - fade to black */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 via-20% to-transparent"></div>
          
          {/* Left side gradient - vignette */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent via-30% to-transparent"></div>
          
          {/* Right side gradient - vignette */}
          <div className="absolute inset-0 bg-gradient-to-l from-black/80 via-transparent via-30% to-transparent"></div>
        </div>

        {/* Footer Columns */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-10 pb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
            
            {/* Brand Column */}
            <div>
              <h3 className="text-lg font-semibold mb-2 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                TANER TORUN
              </h3>
              <p className="text-gray-400 text-xs mb-1">
                M.Hyp. Zihinsel Dönüşüm Mimarı
              </p>
              <p className="text-purple-400/80 text-xs italic mb-4">
                Zihne Fısıldayan Adam
              </p>
              <div className="flex gap-2">
                {socialIcons.map(({ Icon, href, color, label, isSvg }) => (
                  <a 
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-500 ${color} hover:bg-white/10 hover:border-white/20 transition-all duration-300`}
                    aria-label={label}
                    data-testid={`social-link-${label.toLowerCase()}`}
                  >
                    {isSvg ? (
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13.2a8.16 8.16 0 005.58 2.17v-3.44a4.85 4.85 0 01-1.99-.43 4.83 4.83 0 01-1.78-1.31V6.69h3.77z"/></svg>
                    ) : (
                      <Icon className="w-3.5 h-3.5" />
                    )}
                  </a>
                ))}
              </div>
            </div>

            {/* Eğitimlerim */}
            <div>
              <h4 className="text-xs font-semibold text-white mb-4 tracking-wide">Eğitimlerim</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/egitimler" className="group block">
                    <span className="text-gray-400 group-hover:text-purple-400 transition-colors text-xs block">
                      Zihinsel Değişim ve Dönüşüm
                    </span>
                    <span className="text-gray-600 text-[10px]">Online</span>
                  </Link>
                </li>
                <li>
                  <Link to="/egitimler" className="group block">
                    <span className="text-gray-400 group-hover:text-purple-400 transition-colors text-xs block">
                      Zihinsel Değişim ve Dönüşüm
                    </span>
                    <span className="text-gray-600 text-[10px]">Yüzyüze</span>
                  </Link>
                </li>
                <li>
                  <Link to="/masterclass" className="group block">
                    <span className="text-gray-400 group-hover:text-purple-400 transition-colors text-xs block">
                      Masterclass Eğitimi
                    </span>
                    <span className="text-gray-600 text-[10px]">Canlı Zoom</span>
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="group block">
                    <span className="text-gray-400 group-hover:text-purple-400 transition-colors text-xs block">
                      Blog Yazıları
                    </span>
                    <span className="text-gray-600 text-[10px]">Zihinsel Dönüşüm</span>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Uzmanlık Alanlarım */}
            <div>
              <h4 className="text-xs font-semibold text-white mb-4 tracking-wide">Uzmanlık Alanlarım</h4>
              <ul className="space-y-2">
                <li><Link to="/uzmanlik/bilincalti-programlama" className="text-gray-500 hover:text-purple-400 transition-colors text-xs">Bilinçaltı Programlama</Link></li>
                <li><Link to="/uzmanlik/noro-pazarlama" className="text-gray-500 hover:text-pink-400 transition-colors text-xs">Nöropazarlama</Link></li>
                <li><Link to="/uzmanlik/ikna-teknikleri" className="text-gray-500 hover:text-blue-400 transition-colors text-xs">İkna Teknikleri</Link></li>
                <li><Link to="/uzmanlik/is-stratejileri" className="text-gray-500 hover:text-amber-400 transition-colors text-xs">İş Stratejileri</Link></li>
                <li><Link to="/uzmanlik/zihinsel-donusum" className="text-gray-500 hover:text-emerald-400 transition-colors text-xs">Zihinsel Dönüşüm</Link></li>
                <li><Link to="/uzmanlik/derin-ogrenme" className="text-gray-500 hover:text-cyan-400 transition-colors text-xs">Derin Öğrenme</Link></li>
              </ul>
            </div>

            {/* Sirketler */}
            <div>
              <h4 className="text-xs font-semibold text-white mb-4 tracking-wide">Şirketler</h4>
              <ul className="space-y-2">
                <li><a href="https://medyatik.com.tr" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-400 transition-colors text-xs">Medyatik Yazılım A.Ş.</a></li>
                <li><a href="https://medyatik.com.tr" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-400 transition-colors text-xs">Medyatik Interactive 360</a></li>
                <li><a href="https://medyatik.com.tr" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-400 transition-colors text-xs">MindLab Academy</a></li>
              </ul>
            </div>

          </div>
        </div>

        {/* Mountain visible area - showing the peak */}
        <div className="h-[200px] md:h-[280px]"></div>

        {/* Zihinsel Dönüşüm Notu - Global Legal Disclaimer */}
        <div className="relative z-10 border-t border-white/5">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <p className="text-[11px] text-white/20 font-light leading-[1.9] tracking-wide text-center" data-testid="footer-legal-disclaimer">
              <span className="text-white/30 font-medium uppercase tracking-[0.15em] text-[10px]">Zihinsel Dönüşüm Notu</span>
              <span className="mx-2 text-white/10">—</span>
              Bu platformda sunulan tüm içerikler; zihinsel farkındalığı artırmak, düşünce kalıplarını yeniden yapılandırmak ve bireysel dönüşüm süreçlerini desteklemek amacıyla hazırlanmıştır. Burada yer alan hiçbir içerik; tıbbi teşhis koyma, tedavi uygulama veya herhangi bir sağlık hizmeti sunma amacı taşımaz. Fiziksel veya psikolojik sağlıkla ilgili tüm konularda, alanında yetkin sağlık profesyonellerine başvurmanız önerilir.
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="relative z-10 bg-black/70 backdrop-blur-sm border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3">
              <p className="text-gray-600 text-xs">
                © {new Date().getFullYear()} Taner Torun. Tüm hakları saklıdır.
              </p>
              <div className="flex items-center gap-4">
                <Link to="/gizlilik" className="text-gray-600 hover:text-gray-400 transition-colors text-xs">Gizlilik</Link>
                <span className="text-gray-800">•</span>
                <Link to="/yasal-uyari" className="text-gray-600 hover:text-gray-400 transition-colors text-xs">Yasal Uyarı</Link>
                <span className="text-gray-800">•</span>
                <Link to="/kullanim-kosullari" className="text-gray-600 hover:text-gray-400 transition-colors text-xs">Kullanım Koşulları</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
